// 軌跡記録の中核ロジック
// 距離計算(ハーバサイン)、累積標高、平均速度、ポーズ/再開、保存
//
// 設計方針:
//   - 状態を単一のオブジェクトに集約(再生成時に綺麗に初期化)
//   - GPS の精度フィルタ: accuracy > 50m は記録しない(山中の信頼性)
//   - 標高差は隣接2点で 1m 以上の差がある場合のみ累積に加算(GPS 揺らぎ除去)

import { saveTrack } from './cache.js';

// GPS ドリフトを除去するためのフィルタ
// 静止時でも GPS は数 m 単位で座標が揺らぐため、しきい値を厳格化
const ACCURACY_THRESHOLD_M = 30; // 精度 30m 超のサンプルは破棄
const ELEVATION_NOISE_THRESHOLD_M = 2; // 標高差 2m 未満は累積に加えない
const MIN_DISTANCE_M = 5; // 5m 未満の移動は GPS ノイズとみなして無視

function createState() {
  return {
    id: crypto.randomUUID(),
    name: '',
    points: [], // [{ lat, lng, ele, ts, accuracy }]
    startTs: null,
    endTs: null,
    pausedAt: null, // 一時停止開始時刻(ms)
    pauseDurationMs: 0, // 累積一時停止時間
    status: 'idle', // 'idle' | 'recording' | 'paused' | 'stopped'
    stats: {
      distance: 0,
      elevGain: 0,
      elevLoss: 0,
      elevMax: -Infinity,
      elevMin: Infinity,
      duration: 0,
      avgSpeed: 0,
    },
    listeners: new Set(),
  };
}

let state = createState();

// ============ 公開 API ============

export function getStats() {
  // 記録中なら呼ばれる毎に経過時間を再計算
  // (位置情報が来なくても TIME 表示は進むようにする)
  if (state.status === 'recording' && state.startTs) {
    updateDuration();
  }
  return { ...state.stats, status: state.status, pointCount: state.points.length };
}

export function getPoints() {
  return state.points.slice();
}

export function getStatus() {
  return state.status;
}

export function subscribe(fn) {
  state.listeners.add(fn);
  return () => state.listeners.delete(fn);
}

function notify() {
  state.listeners.forEach((fn) => {
    try {
      fn(getStats(), getPoints());
    } catch (e) {
      console.warn('tracker listener failed', e);
    }
  });
}

export function start() {
  if (state.status === 'recording') return;
  state = createState();
  state.status = 'recording';
  state.startTs = Date.now();
  notify();
}

export function pause() {
  if (state.status !== 'recording') return;
  state.status = 'paused';
  state.pausedAt = Date.now();
  notify();
}

export function resume() {
  if (state.status !== 'paused') return;
  if (state.pausedAt) {
    state.pauseDurationMs += Date.now() - state.pausedAt;
    state.pausedAt = null;
  }
  state.status = 'recording';
  notify();
}

// 停止して保存。保存した track を返す
export async function stop(name) {
  if (state.status === 'idle' || state.status === 'stopped') return null;
  state.endTs = Date.now();
  // 一時停止中だった場合、その分も加算
  if (state.pausedAt) {
    state.pauseDurationMs += state.endTs - state.pausedAt;
    state.pausedAt = null;
  }
  state.status = 'stopped';
  updateDuration();

  // 1点未満なら保存しない
  if (state.points.length === 0) {
    notify();
    return null;
  }

  const track = {
    id: state.id,
    name: name || defaultTrackName(state.startTs),
    points: state.points,
    stats: { ...state.stats },
    startTs: state.startTs,
    endTs: state.endTs,
  };
  const saved = await saveTrack(track);
  notify();
  return saved;
}

// 記録途中の破棄(保存せずに状態を捨てる)
export function discard() {
  state = createState();
  notify();
}

// 新しいGPS座標を追加。状態を更新して true を返す(無効・記録外なら false)
export function addPoint({ lat, lng, ele, ts, accuracy }) {
  if (state.status !== 'recording') return false;
  if (typeof lat !== 'number' || typeof lng !== 'number') return false;
  if (typeof accuracy === 'number' && accuracy > ACCURACY_THRESHOLD_M) return false;

  const point = {
    lat,
    lng,
    ele: typeof ele === 'number' ? ele : null,
    ts: ts || Date.now(),
    accuracy: accuracy || null,
  };

  // 直前の点と比較して距離が極小なら無視
  const last = state.points[state.points.length - 1];
  if (last) {
    const d = haversineMeters(last.lat, last.lng, lat, lng);
    if (d < MIN_DISTANCE_M) return false;
    state.stats.distance += d;

    // 標高差
    if (point.ele !== null && last.ele !== null) {
      const diff = point.ele - last.ele;
      if (Math.abs(diff) >= ELEVATION_NOISE_THRESHOLD_M) {
        if (diff > 0) state.stats.elevGain += diff;
        else state.stats.elevLoss += -diff;
      }
    }
  }

  // 最大・最小標高
  if (point.ele !== null) {
    if (point.ele > state.stats.elevMax) state.stats.elevMax = point.ele;
    if (point.ele < state.stats.elevMin) state.stats.elevMin = point.ele;
  }

  state.points.push(point);
  updateDuration();
  notify();
  return true;
}

// 経過時間と平均速度を再計算
function updateDuration() {
  if (!state.startTs) return;
  const endTs = state.endTs || Date.now();
  const elapsed = Math.max(0, endTs - state.startTs - state.pauseDurationMs);
  state.stats.duration = Math.floor(elapsed / 1000);
  if (state.stats.duration > 0) {
    state.stats.avgSpeed = state.stats.distance / state.stats.duration;
  }
  // 直近 60 秒の歩行ペース(現在速度)
  state.stats.currentPace = computeRecentPace();
}

// 直近 60 秒の点から現在の速度を計算 (m/s)
function computeRecentPace() {
  if (state.points.length < 2) return 0;
  const now = Date.now();
  const cutoff = now - 60 * 1000;
  // cutoff 以降の点を集める
  const recent = state.points.filter((p) => (p.ts || 0) >= cutoff);
  if (recent.length < 2) return 0;
  let dist = 0;
  for (let i = 1; i < recent.length; i++) {
    dist += haversineMeters(recent[i - 1].lat, recent[i - 1].lng, recent[i].lat, recent[i].lng);
  }
  const dt = (recent[recent.length - 1].ts - recent[0].ts) / 1000;
  if (dt <= 0) return 0;
  return dist / dt;
}

// ============ ヘルパー ============

function defaultTrackName(startTs) {
  const d = new Date(startTs);
  return d.toLocaleDateString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).replace(/\//g, '.') + ' の登山';
}

// ハーバサイン距離(m)
export function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6378137; // 地球半径(m)
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// ============ Wake Lock(画面常時オン) ============

let wakeLock = null;

export async function acquireWakeLock() {
  if (!('wakeLock' in navigator)) return false;
  try {
    wakeLock = await navigator.wakeLock.request('screen');
    return true;
  } catch (e) {
    console.warn('Wake Lock acquire failed', e);
    return false;
  }
}

export async function releaseWakeLock() {
  if (wakeLock) {
    try {
      await wakeLock.release();
    } catch (e) {
      /* noop */
    }
    wakeLock = null;
  }
}

// 可視性復帰時に Wake Lock を再取得(iOS は自動解除されるため)
document.addEventListener('visibilitychange', async () => {
  if (document.visibilityState === 'visible' && state.status === 'recording') {
    await acquireWakeLock();
  }
});
